import { Chess } from "@vendor/chess.js";
import { computeCenterControl } from "./boardOverlays.ts";
import { moveSafety } from "../chess/exchange.ts";
import { engineApproves, hasEngineLines } from "./engineLines.ts";

/**
 * A calm, structural reading of a position: who stands where and why it
 * matters. This is board observation, not tactical proof. The engine axes
 * keep responsibility for evaluation; this layer gives the student the
 * vocabulary a coach uses between moves (material, king, development,
 * center, files, space, breaks, character).
 */

export type KingState =
  "castled kingside" | "castled queenside" | "still in the center" | "moved without castling";

export type PositionCharacter = "open" | "semi-open" | "closed";

export type PositionReading = {
  playerSide?: "w" | "b";
  sideToMove: "w" | "b";
  material: { white: number; black: number; diff: number; label: string };
  king: { white: KingState; black: KingState; whiteCanCastle: boolean; blackCanCastle?: boolean };
  development: {
    whiteOut: number;
    blackOut: number;
    whiteTotal: number;
    blackTotal: number;
    whiteUndeveloped: string[];
    whiteRooksConnected: boolean;
    blackUndeveloped?: string[];
    blackRooksConnected?: boolean;
  };
  center: {
    whitePawns: string[];
    blackPawns: string[];
    net: number;
    label: string;
  };
  files: { open: string[]; halfOpenWhite: string[]; halfOpenBlack: string[] };
  space: { white: number; black: number; label: string };
  bishops: { whitePair: boolean; blackPair: boolean };
  pawnBreaks: string[];
  /** A pawn of one side attacks a pawn of the other, so an exchange is available. */
  pawnTension: boolean;
  /** Some pawns have left the board. */
  pawnsTraded: boolean;
  looseWhite: string[];
  looseBlack?: string[];
  blackAttacks: string[];
  inCheck: boolean;
  character: PositionCharacter;
  sentences: string[];
};

type BoardPiece = { square: string; type: string; color: string };

const VALUE: Record<string, number> = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 0 };
const NAME: Record<string, string> = {
  p: "pawn",
  n: "knight",
  b: "bishop",
  r: "rook",
  q: "queen",
  k: "king",
};
const STARTING: Record<string, string> = { b1: "n", g1: "n", c1: "b", f1: "b" };
const STARTING_BLACK: Record<string, string> = { b8: "n", g8: "n", c8: "b", f8: "b" };
const FILES = "abcdefgh";
const CENTER_SQUARES = [
  "c3",
  "c4",
  "c5",
  "c6",
  "d3",
  "d4",
  "d5",
  "d6",
  "e3",
  "e4",
  "e5",
  "e6",
  "f3",
  "f4",
  "f5",
  "f6",
];

function pieces(chess: Chess): BoardPiece[] {
  return chess
    .board()
    .flatMap((rank, row) =>
      rank.flatMap((piece, file) =>
        piece ? [{ ...piece, square: `${FILES[file]}${8 - row}` }] : []
      )
    );
}

function kingState(chess: Chess, color: "w" | "b", board: BoardPiece[]): KingState {
  const king = board.find((p) => p.color === color && p.type === "k");
  const home = color === "w" ? "e1" : "e8";
  const rank = color === "w" ? "1" : "8";
  if (!king) return "still in the center";
  if (king.square === home) return "still in the center";
  const rookFile = (sq: string) =>
    board.find((p) => p.square === sq && p.color === color && p.type === "r");
  if (king.square === `g${rank}` && rookFile(`f${rank}`)) return "castled kingside";
  if (king.square === `c${rank}` && rookFile(`d${rank}`)) return "castled queenside";
  // A king on g1/c1 after a later rook move still counts as castled.
  if (king.square === `g${rank}` || king.square === `h${rank}`) {
    const castling = chess.fen().split(" ")[2] || "";
    const rights = color === "w" ? /[KQ]/ : /[kq]/;
    if (!rights.test(castling)) return "castled kingside";
  }
  if (king.square === `c${rank}` || king.square === `b${rank}`) return "castled queenside";
  return "moved without castling";
}

function fileState(board: BoardPiece[]): PositionReading["files"] {
  const open: string[] = [];
  const halfOpenWhite: string[] = [];
  const halfOpenBlack: string[] = [];
  for (const file of FILES) {
    const pawns = board.filter((p) => p.type === "p" && p.square[0] === file);
    const white = pawns.some((p) => p.color === "w");
    const black = pawns.some((p) => p.color === "b");
    if (!white && !black) open.push(file);
    else if (!white && black) halfOpenWhite.push(file);
    else if (white && !black) halfOpenBlack.push(file);
  }
  return { open, halfOpenWhite, halfOpenBlack };
}

function spaceCount(chess: Chess, color: "w" | "b"): number {
  // Squares in the opponent's half that this side attacks. A blunt but honest
  // measure of territory that a student can verify by looking at the board.
  const ranks = color === "w" ? ["5", "6", "7", "8"] : ["1", "2", "3", "4"];
  let count = 0;
  for (const file of FILES) {
    for (const rank of ranks) {
      if (chess.isAttacked(`${file}${rank}`, color)) count += 1;
    }
  }
  return count;
}

function pawnBreaks(chess: Chess, board: BoardPiece[], color: "w" | "b" = "w"): string[] {
  if (chess.turn() !== color) return [];
  const blackPawnSquares = new Set(
    board
      .filter((p) => p.color === (color === "w" ? "b" : "w") && p.type === "p")
      .map((p) => p.square)
  );
  // A white pawn on `to` and a black pawn diagonally ahead of it attack each
  // other, so one test covers both directions of the contact.
  const createsContact = (to: string) => {
    const file = to.charCodeAt(0) - 97;
    const rank = Number(to[1]) + (color === "w" ? 1 : -1);
    return [file - 1, file + 1].some(
      (f) => f >= 0 && f < 8 && blackPawnSquares.has(`${FILES[f]}${rank}`)
    );
  };
  const contacts = chess
    .moves({ verbose: true })
    .filter((m) => m.piece === "p" && !m.captured && "bcdefg".includes(m.to[0]))
    .filter((m) => createsContact(m.to));
  // A break that loses material is not a plan to weigh. The engine judges
  // when it has scored the position; otherwise the exchange count does.
  const fen = chess.fen();
  const safety = hasEngineLines(fen) ? null : moveSafety(chess, contacts);
  const breaks = contacts
    .filter((m) => (safety ? (safety.get(m)?.net ?? -1) >= 0 : engineApproves(fen, m) === true))
    .map((m) => m.san);
  return [...new Set(breaks)].slice(0, 3);
}

/** Whether a pawn of one side attacks a pawn of the other. */
function hasPawnTension(board: BoardPiece[]): boolean {
  const at = new Set(board.filter((p) => p.type === "p").map((p) => `${p.color}${p.square}`));
  // Central tension: both pawns on the c- to f-files (a4 against b5 is a wing contact).
  const central = (f: number) => f >= 2 && f <= 5;
  return board.some((p) => {
    if (p.type !== "p" || p.color !== "w") return false;
    const file = p.square.charCodeAt(0) - 97;
    const rank = Number(p.square[1]) + 1;
    return (
      central(file) && [file - 1, file + 1].some((f) => central(f) && at.has(`b${FILES[f]}${rank}`))
    );
  });
}

/** "a, b and c". */
function joinList(items: string[]): string {
  return items.length > 1 ? `${items.slice(0, -1).join(", ")} and ${items.at(-1)}` : items[0] || "";
}

function capitalize(text: string): string {
  return text ? text[0].toUpperCase() + text.slice(1) : text;
}

function characterOf(board: BoardPiece[], files: PositionReading["files"]): PositionCharacter {
  const pawns = board.filter((p) => p.type === "p");
  const whitePawns = new Set(pawns.filter((p) => p.color === "w").map((p) => p.square));
  const blackPawns = new Set(pawns.filter((p) => p.color === "b").map((p) => p.square));
  let lockedCentral = 0;
  for (const file of "cdef") {
    for (let rank = 2; rank <= 6; rank += 1) {
      if (whitePawns.has(`${file}${rank}`) && blackPawns.has(`${file}${rank + 1}`)) {
        lockedCentral += 1;
      }
    }
  }
  if (lockedCentral >= 2) return "closed";
  if (pawns.length <= 12 || files.open.length >= 2) return "open";
  return "semi-open";
}

function listFiles(files: string[]): string {
  if (!files.length) return "";
  if (files.length === 1) return `the ${files[0]}-file`;
  if (files.length === 2) return `the ${files[0]}- and ${files[1]}-files`;
  return `the ${files.slice(0, -1).join("-, ")}- and ${files[files.length - 1]}-files`;
}

const MATERIAL_WORDS: Array<[string, string, string]> = [
  ["q", "a queen", "queens"],
  ["r", "a rook", "rooks"],
  ["m", "a minor piece", "minor pieces"],
  ["p", "a pawn", "pawns"],
];
const COUNT_WORDS = ["", "", "two", "three", "four", "five", "six", "seven", "eight"];

/** The pieces one side has beyond the other's: "a rook and two pawns". */
function extraMaterial(extra: Record<string, number>): string {
  const parts = MATERIAL_WORDS.filter(([type]) => extra[type] > 0).map(([type, one, many]) =>
    extra[type] === 1 ? one : `${COUNT_WORDS[extra[type]] || extra[type]} ${many}`
  );
  return parts.length > 1 ? `${parts.slice(0, -1).join(", ")} and ${parts.at(-1)}` : parts[0];
}

/**
 * The material balance in words, from the pieces themselves: "White is a
 * rook up", "Black has a bishop for a pawn", not a point count that can
 * hide what was traded.
 */
function materialLabel(board: BoardPiece[], diff: number): string {
  const count = (color: string, types: string) =>
    board.filter((p) => p.color === color && types.includes(p.type)).length;
  const whiteExtra: Record<string, number> = {};
  const blackExtra: Record<string, number> = {};
  for (const [type, types] of [
    ["q", "q"],
    ["r", "r"],
    ["m", "nb"],
    ["p", "p"],
  ]) {
    const balance = count("w", types) - count("b", types);
    if (balance > 0) whiteExtra[type] = balance;
    if (balance < 0) blackExtra[type] = -balance;
  }
  const white = extraMaterial(whiteExtra);
  const black = extraMaterial(blackExtra);
  if (!white && !black) return "Material is level.";
  if (!black) return `White is ${white} up.`;
  if (!white) return `Black is ${black} up.`;
  if (diff > 0) return `White has ${white} for ${black}.`;
  if (diff < 0) return `Black has ${black} for ${white}.`;
  return `Material is level in value: White has ${white} for ${black}.`;
}

// The scale of computeCenterControl: 1.e4 scores +4, a balanced opening ±1.
function centerLabel(net: number): string {
  if (net >= 4) return "White has the stronger grip on the center.";
  if (net >= 2) return "White has a slight edge in central control.";
  if (net <= -4) return "Black has the stronger grip on the center.";
  if (net <= -2) return "Black has a slight edge in central control.";
  return "Central control is balanced.";
}

function spaceLabel(white: number, black: number): string {
  const diff = white - black;
  if (diff >= 5) return "White has more space.";
  if (diff <= -5) return "Black has more space.";
  return "Space is roughly equal.";
}

function kingSentence(state: KingState, color: "White" | "Black", canCastle: boolean): string {
  if (state === "castled kingside") return `${color}'s king is castled on the kingside.`;
  if (state === "castled queenside") return `${color}'s king is castled on the queenside.`;
  if (state === "still in the center") {
    return canCastle
      ? `${color}'s king is still in the center, with castling available.`
      : `${color}'s king is still in the center.`;
  }
  return `${color}'s king has moved without castling.`;
}

export function readPosition(fen: string, playerSide: "w" | "b" = "w"): PositionReading {
  const chess = new Chess(fen);
  const board = pieces(chess);
  const material = {
    white: board.filter((p) => p.color === "w").reduce((t, p) => t + VALUE[p.type], 0),
    black: board.filter((p) => p.color === "b").reduce((t, p) => t + VALUE[p.type], 0),
  };
  const diff = material.white - material.black;
  const castling = fen.split(" ")[2] || "";
  const whiteCanCastle = /[KQ]/.test(castling);
  const king = {
    white: kingState(chess, "w", board),
    black: kingState(chess, "b", board),
    whiteCanCastle,
    blackCanCastle: /[kq]/.test(castling),
  };
  const whiteUndeveloped = board
    .filter((p) => p.color === "w" && STARTING[p.square] === p.type)
    .map((p) => `${NAME[p.type]} on ${p.square}`);
  const blackUndeveloped = board.filter(
    (p) => p.color === "b" && STARTING_BLACK[p.square] === p.type
  );
  const whiteTotal = board.filter(
    (p) => p.color === "w" && (p.type === "n" || p.type === "b")
  ).length;
  const blackTotal = board.filter(
    (p) => p.color === "b" && (p.type === "n" || p.type === "b")
  ).length;
  const rooks = board.filter((p) => p.color === "w" && p.type === "r" && p.square[1] === "1");
  let whiteRooksConnected = false;
  if (rooks.length === 2) {
    const [a, b] = rooks.map((r) => r.square.charCodeAt(0) - 97).sort((x, y) => x - y);
    whiteRooksConnected = true;
    for (let f = a + 1; f < b; f += 1) {
      if (board.some((p) => p.square === `${FILES[f]}1`)) whiteRooksConnected = false;
    }
  }
  const blackHome = blackUndeveloped.map((p) => `${NAME[p.type]} on ${p.square}`);
  const blackRooks = board.filter((p) => p.color === "b" && p.type === "r" && p.square[1] === "8");
  const blackRooksConnected =
    blackRooks.length === 2 &&
    !board.some(
      (p) =>
        p.square[1] === "8" &&
        p.square[0] > blackRooks.map((r) => r.square[0]).sort()[0] &&
        p.square[0] < blackRooks.map((r) => r.square[0]).sort()[1]
    );
  const looseBlack = board
    .filter(
      (p) =>
        p.color === "b" &&
        p.type !== "k" &&
        chess.isAttacked(p.square, "w") &&
        !chess.isAttacked(p.square, "b")
    )
    .map((p) => `${NAME[p.type]} on ${p.square}`);
  const ownUndeveloped = playerSide === "w" ? whiteUndeveloped : blackHome;
  const ownTotal = playerSide === "w" ? whiteTotal : blackTotal;
  const ownConnected = playerSide === "w" ? whiteRooksConnected : blackRooksConnected;
  const ownName = playerSide === "w" ? "White" : "Black";
  const control = computeCenterControl(fen);
  const whiteCenterPawns = board
    .filter((p) => p.color === "w" && p.type === "p" && CENTER_SQUARES.includes(p.square))
    .map((p) => p.square);
  const blackCenterPawns = board
    .filter((p) => p.color === "b" && p.type === "p" && CENTER_SQUARES.includes(p.square))
    .map((p) => p.square);
  const files = fileState(board);
  const space = { white: spaceCount(chess, "w"), black: spaceCount(chess, "b") };
  const bishops = {
    whitePair: board.filter((p) => p.color === "w" && p.type === "b").length >= 2,
    blackPair: board.filter((p) => p.color === "b" && p.type === "b").length >= 2,
  };
  const looseWhite = board
    .filter(
      (p) =>
        p.color === "w" &&
        p.type !== "k" &&
        chess.isAttacked(p.square, "b") &&
        !chess.isAttacked(p.square, "w")
    )
    .map((p) => `${NAME[p.type]} on ${p.square}`);
  const blackAttacks = board
    .filter(
      (p) => p.color === "w" && p.type !== "k" && p.type !== "p" && chess.isAttacked(p.square, "b")
    )
    .map((p) => `${NAME[p.type]} on ${p.square}`);
  const ownKing = board.find((p) => p.color === playerSide && p.type === "k");
  const inCheck = Boolean(
    ownKing && chess.isAttacked(ownKing.square, playerSide === "w" ? "b" : "w")
  );
  const character = characterOf(board, files);
  const pawnTension = hasPawnTension(board);
  const pawnsTraded = board.filter((p) => p.type === "p").length < 16;
  const breaks = pawnBreaks(chess, board, playerSide);

  const sentences: string[] = [];
  sentences.push(materialLabel(board, diff));
  sentences.push(
    kingSentence(
      playerSide === "w" ? king.white : king.black,
      ownName,
      playerSide === "w" ? whiteCanCastle : Boolean(king.blackCanCastle)
    )
  );
  sentences.push(
    ownUndeveloped.length === 0
      ? `${ownTotal === 0 ? `${ownName} has no minor pieces remaining` : `All remaining ${ownName} minor pieces are developed`}${ownConnected ? " and the rooks are connected" : ""}.`
      : `${ownName} still has ${ownUndeveloped.length === 1 ? "one minor piece" : `${ownUndeveloped.length} minor pieces`} at home: ${joinList(ownUndeveloped)}.`
  );
  sentences.push(
    `${centerLabel(control.net)} ${
      character === "closed"
        ? "The center is closed, so plans run through pawn breaks and slow piece improvement."
        : character === "open"
          ? "The center is open, so piece activity and open files decide the play."
          : pawnTension
            ? "Pawns are in contact in the center, so the next exchanges will shape the middlegame."
            : pawnsTraded
              ? "The center is half-open: some pawns have been traded, and the remaining pawns decide which plans fit."
              : "No pawns have been traded yet, so a pawn break will decide which lines open."
    }`
  );
  if (
    files.open.length ||
    (playerSide === "w" ? files.halfOpenWhite : files.halfOpenBlack).length
  ) {
    const parts: string[] = [];
    if (files.open.length)
      parts.push(`${listFiles(files.open)} ${files.open.length === 1 ? "is" : "are"} fully open`);
    if ((playerSide === "w" ? files.halfOpenWhite : files.halfOpenBlack).length)
      parts.push(
        `${listFiles(playerSide === "w" ? files.halfOpenWhite : files.halfOpenBlack)} ${(playerSide === "w" ? files.halfOpenWhite : files.halfOpenBlack).length === 1 ? "is" : "are"} half-open for ${ownName}`
      );
    sentences.push(
      `${capitalize(parts.join("; "))}. A rook needs an entry square as well as a file.`
    );
  } else {
    sentences.push("No file has opened yet; the rooks are waiting for a pawn exchange.");
  }
  if (breaks.length) {
    sentences.push(
      breaks.length === 1
        ? `Pawn break to weigh: ${breaks[0]}. It changes which files and diagonals open.`
        : `Pawn breaks to weigh: ${joinList(breaks)}. Each changes which files and diagonals open.`
    );
  }
  if (inCheck) {
    sentences.push(
      `${chess.turn() === "w" ? "White" : "Black"} is in check; that comes before every plan.`
    );
  } else if ((playerSide === "w" ? looseWhite : looseBlack).length) {
    sentences.push(
      `Loose for ${ownName}: ${(playerSide === "w" ? looseWhite : looseBlack).join(", ")}. Count defenders before a quiet move.`
    );
  }

  return {
    playerSide,
    sideToMove: chess.turn(),
    material: { ...material, diff, label: materialLabel(board, diff) },
    king,
    development: {
      whiteOut: whiteTotal - whiteUndeveloped.length,
      blackOut: blackTotal - blackUndeveloped.length,
      whiteTotal,
      blackTotal,
      whiteUndeveloped,
      whiteRooksConnected,
      blackUndeveloped: blackHome,
      blackRooksConnected,
    },
    center: {
      whitePawns: whiteCenterPawns,
      blackPawns: blackCenterPawns,
      net: control.net,
      label: centerLabel(control.net),
    },
    files,
    space: { ...space, label: spaceLabel(space.white, space.black) },
    bishops,
    pawnBreaks: breaks,
    pawnTension,
    pawnsTraded,
    looseWhite,
    looseBlack,
    blackAttacks,
    inCheck,
    character,
    sentences,
  };
}

/** Short, labelled facts for a compact two-column display. */
export function readingFacts(reading: PositionReading): Array<{ label: string; value: string }> {
  const kingWord = (state: KingState) =>
    state === "castled kingside"
      ? "Castled (K-side)"
      : state === "castled queenside"
        ? "Castled (Q-side)"
        : state === "still in the center"
          ? "In the center"
          : "Moved, not castled";
  return [
    {
      label: "Material",
      value:
        reading.material.diff === 0
          ? "Level"
          : `${reading.material.diff > 0 ? "White" : "Black"} +${Math.abs(reading.material.diff)}`,
    },
    { label: "White king", value: kingWord(reading.king.white) },
    { label: "Black king", value: kingWord(reading.king.black) },
    {
      label: "Development",
      value: `${reading.playerSide === "b" ? reading.development.blackOut : reading.development.whiteOut} of ${reading.playerSide === "b" ? reading.development.blackTotal : reading.development.whiteTotal} minor pieces developed${(reading.playerSide === "b" ? reading.development.blackRooksConnected : reading.development.whiteRooksConnected) ? ", rooks connected" : ""}`,
    },
    {
      label: "Center",
      value:
        Math.abs(reading.center.net) < 2
          ? "Balanced"
          : `${reading.center.net > 0 ? "White" : "Black"} by ${Math.abs(reading.center.net)}`,
    },
    {
      label: "Files",
      value: reading.files.open.length
        ? `Open: ${reading.files.open.join(", ")}`
        : (reading.playerSide === "b" ? reading.files.halfOpenBlack : reading.files.halfOpenWhite)
              .length
          ? `Half-open: ${(reading.playerSide === "b" ? reading.files.halfOpenBlack : reading.files.halfOpenWhite).join(", ")}`
          : "None open yet",
    },
    {
      label: "Space",
      value:
        reading.space.white === reading.space.black
          ? "Even"
          : reading.space.white > reading.space.black
            ? `White (${reading.space.white} v ${reading.space.black})`
            : `Black (${reading.space.black} v ${reading.space.white})`,
    },
    {
      label: "Character",
      value:
        reading.character === "semi-open"
          ? reading.pawnsTraded
            ? "Half-open"
            : "No pawns traded yet"
          : reading.character[0].toUpperCase() + reading.character.slice(1),
    },
  ];
}
